/**
 * Firebase configuration for Identity Platform authentication
 *
 * This config connects to Google Cloud Identity Platform which provides
 * Firebase Auth compatible authentication with support for:
 * - Email/Password Sign-In (with registration and password reset)
 * - Google Sign-In (OAuth)
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User,
  UserCredential,
} from 'firebase/auth';

// Firebase configuration
// These values come from Google Cloud Identity Platform console
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'datawarehouse-dev-sl.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'datawarehouse-dev-sl',
};

// Initialize Firebase
let app: FirebaseApp;
let auth: Auth;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  console.log('Firebase initialized for project:', firebaseConfig.projectId);
} catch (error) {
  console.error('Failed to initialize Firebase:', error);
  throw error;
}

// Auth providers
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account', // Always show account selector
});

/**
 * Sign in with Google
 */
export async function signInWithGoogle(): Promise<User> {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    console.log('Signed in with Google:', result.user.email);
    return result.user;
  } catch (error: unknown) {
    const authError = error as { code?: string; message?: string };
    console.error('Google sign-in error:', authError.code, authError.message);
    throw error;
  }
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(email: string, password: string): Promise<User> {
  try {
    const result: UserCredential = await signInWithEmailAndPassword(auth, email, password);
    console.log('Signed in with email:', result.user.email);
    return result.user;
  } catch (error: unknown) {
    const authError = error as { code?: string; message?: string };
    console.error('Email sign-in error:', authError.code, authError.message);
    throw error;
  }
}

/**
 * Create a new account with email and password
 */
export async function createAccount(email: string, password: string, displayName?: string): Promise<User> {
  try {
    const result: UserCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Update display name if provided
    if (displayName && result.user) {
      await updateProfile(result.user, { displayName });
    }

    console.log('Account created for:', result.user.email);
    return result.user;
  } catch (error: unknown) {
    const authError = error as { code?: string; message?: string };
    console.error('Account creation error:', authError.code, authError.message);
    throw error;
  }
}

/**
 * Send password reset email
 */
export async function resetPassword(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email);
    console.log('Password reset email sent to:', email);
  } catch (error: unknown) {
    const authError = error as { code?: string; message?: string };
    console.error('Password reset error:', authError.code, authError.message);
    throw error;
  }
}

/**
 * Sign out
 */
export async function signOut(): Promise<void> {
  try {
    await firebaseSignOut(auth);
    console.log('Signed out');
  } catch (error) {
    console.error('Sign-out error:', error);
    throw error;
  }
}

/**
 * Get current ID token for API requests
 */
export async function getIdToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }
  try {
    return await user.getIdToken();
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
}

/**
 * Subscribe to auth state changes
 */
export function onAuthStateChange(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, callback);
}

/**
 * Get user-friendly error message from Firebase auth error code
 */
export function getAuthErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/operation-not-allowed':
      return 'Email/password accounts are not enabled.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
      return 'No account found with this email.';
    case 'auth/wrong-password':
      return 'Incorrect password.';
    case 'auth/invalid-credential':
      return 'Invalid email or password.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    default:
      return 'An error occurred. Please try again.';
  }
}

export { auth };
export type { User };
